import React, { useState, useEffect, useMemo } from 'react';
import {
    Grid, Card, CardContent, Typography, CircularProgress, Box,
    FormControl, Select, MenuItem, FormGroup, FormControlLabel,
    Checkbox, styled
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

// 1. Import Firestore functions for getting a collection
import { db } from '../Firebase';
import { collection, getDocs } from "firebase/firestore";

// Internal imports
import TrendLineChart from './TrendLineChart';
import GroupedBarChart from './GroupedBarChart';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SchoolIcon from '@mui/icons-material/School';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import './Dashboard.css';

// Constants and Styled Components
const OFFICE_COLS = { COURSE: 'Couse', INCOME: 'เงินรับจริง', DATE: 'วันที่' };
const SCHOOL_COLS = { SCHOOL_NAME: 'โรงเรียน', INCOME: 'จำนวนเงิน', DATE: 'วันที่' };

const StyledSelect = styled(Select)(({ theme }) => ({
  backgroundColor: '#fff',
  borderRadius: '12px',
  boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
}));

const StatCardContainer = styled(Card)(({ theme, bgcolor }) => ({
  background: bgcolor,
  borderRadius: '16px',
  color: '#fff',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 20px rgba(0,0,0,0.15)'
  }
}));

const GradientHeader = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(45deg, #3f51b5 30%, #2196f3 90%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: '800',
  textAlign: 'center',
  margin: 0,
  padding: '0.5rem',
  letterSpacing: '1px'
}));

const thaiMonths = { 'ม.ค.': 0, 'ก.พ.': 1, 'มี.ค.': 2, 'เม.ย.': 3, 'พ.ค.': 4, 'มิ.ย.': 5, 'ก.ค.': 6, 'ส.ค.': 7, 'ก.ย.': 8, 'ต.ค.': 9, 'พ.ย.': 10, 'ธ.ค.': 11 };
const parseDate = (dateVal) => {
    try {
        if (!dateVal) return null;
        // Handle Firestore Timestamp
        if (dateVal && typeof dateVal.toDate === 'function') {
            return dateVal.toDate();
        }
        
        if (typeof dateVal !== 'string') return new Date(dateVal);

        if (dateVal.includes('/')) {
            const parts = dateVal.split('/');
            if (parts.length === 3) {
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                let year = parseInt(parts[2], 10);
                if (year < 100) { year += 2000; }
                if (!isNaN(day) && !isNaN(month) && !isNaN(year)) return new Date(year, month, day);
            }
        }
        const parts = dateVal.split(' ');
        if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = thaiMonths[parts[1]];
            const year = parseInt(parts[2], 10) + 2000;
            if (!isNaN(day) && month !== undefined && !isNaN(year)) return new Date(year, month, day);
        }
        return new Date(dateVal);
    } catch { return null; }
};

const StatCard = ({ title, value, icon, bgColor }) => (
  <StatCardContainer bgcolor={bgColor}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2.5 }}>
      <Typography variant="subtitle1" sx={{ opacity: 0.9, fontWeight: 600 }}>{title}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 50, height: 50, borderRadius: '50%', backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
        {icon}
      </Box>
    </Box>
    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', px: 2.5, pb: 2.5 }}>
      <Typography variant="h3" sx={{ fontWeight: 'bold' }}>{value}</Typography>
    </Box>
  </StatCardContainer>
);


const Dashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    
    // State for year selection
    const [availableYears, setAvailableYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState('');

    const [officeData, setOfficeData] = useState([]);
    const [schoolData, setSchoolData] = useState([]);
    const [viewData, setViewData] = useState({ revenue: 0, registrations: 0, chartData: [], subjects: [] });
    const [courseList, setCourseList] = useState([]);
    const [monthList, setMonthList] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState('All');
    const [selectedMonth, setSelectedMonth] = useState('All');
    const [dataSources, setDataSources] = useState({ office: true, school: true });

    // useEffect to fetch and set available years from data
    useEffect(() => {
        if (loading || (officeData.length === 0 && schoolData.length === 0)) return;

        const allYears = new Set();
        
        // Extract years from combined data
        const dataToProcess = [...officeData, ...schoolData];
        dataToProcess.forEach(item => {
            if (item.parsedDate && typeof item.parsedDate.getFullYear === 'function') {
                allYears.add(item.parsedDate.getFullYear());
            }
        });

        const sortedYears = Array.from(allYears).sort((a, b) => b - a);
        setAvailableYears(sortedYears);

        // Set default selected year if not set or if current selection is invalid
        if (sortedYears.length > 0 && (!selectedYear || !sortedYears.includes(parseInt(selectedYear)))) {
            setSelectedYear(sortedYears[0]);
        }
    }, [officeData, schoolData, loading, selectedYear, setSelectedYear]);

    // useEffect to fetch data from Firestore collections
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch Office Receipts
                const officeSnapshot = await getDocs(collection(db, "office-recipt"));
                const officeMapped = officeSnapshot.docs.map(doc => {
                    const data = doc.data();
                    const rawDate = data[OFFICE_COLS.DATE] || data['date'] || data['Date'];
                    const parsedDate = parseDate(rawDate);
                    // Handle various income field names (updated to include 'real-amount')
                    const rawIncome = data[OFFICE_COLS.INCOME] || data['real-amount'] || data['income'] || data['amount'] || 0;
                    const parsedIncome = parseFloat(String(rawIncome).replace(/,/g, '')) || 0;
                    
                    if (!parsedDate || isNaN(parsedDate.getTime()) || parsedIncome <= 0) return null;

                    return {
                        ...data,
                        type: 'office',
                        [OFFICE_COLS.COURSE]: data['couse'] || data['course'] || data['Couse'] || data[OFFICE_COLS.COURSE] || 'Unknown Course',
                        parsedDate,
                        parsedIncome
                    };
                }).filter(Boolean);
                setOfficeData(officeMapped);

                // Fetch School Receipts
                const schoolSnapshot = await getDocs(collection(db, "school-recipt"));
                const schoolMapped = schoolSnapshot.docs.map((doc, index) => {
                    const data = doc.data();
                    // Handle School fields for Dashboard
                    const rawDate = data[SCHOOL_COLS.DATE] || data['date'] || data['Date'];
                    const parsedDate = parseDate(rawDate);
                    const rawIncome = data[SCHOOL_COLS.INCOME] || data['amount'] || data['income'] || 0;
                    const parsedIncome = parseFloat(String(rawIncome).replace(/,/g, '')) || 0;
                    
                    if (!parsedDate || isNaN(parsedDate.getTime()) || parsedIncome <= 0) return null;

                    // Convert to BE if year is AD (likely < 2400)
                    if (parsedDate.getFullYear() < 2400) {
                        parsedDate.setFullYear(parsedDate.getFullYear() + 543);
                    }

                    return {
                        type: 'school',
                        [OFFICE_COLS.COURSE]: data['customer'] || data['Customer'] || data['school-recipt'] || data['School-Recipt'] || data[SCHOOL_COLS.SCHOOL_NAME] || 'รายรับโรงเรียน',
                        'รหัส': data['school-ID'] || `school_${index}`, // Use school-ID or fallback
                        'วันที่': rawDate,
                        'เงินรับจริง': rawIncome,
                        parsedDate,
                        parsedIncome
                    };
                }).filter(Boolean);
                setSchoolData(schoolMapped);

            } catch (error) {
                console.error("Error fetching dashboard data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []); // Fetch once on mount

    const combinedData = useMemo(() => {
        let data = [];
        if (dataSources.office) data = [...data, ...officeData];
        if (dataSources.school) data = [...data, ...schoolData];
        return data;
    }, [officeData, schoolData, dataSources]);

    // Filter by Year first (to populate dropdowns)
    const yearFilteredData = useMemo(() => {
        if (combinedData.length === 0) return [];
        if (!selectedYear) return combinedData;
        return combinedData.filter(row => row.parsedDate.getFullYear() === parseInt(selectedYear));
    }, [combinedData, selectedYear]);

    // Filter by Course and Month (for charts/stats)
    const filteredData = useMemo(() => {
        if (yearFilteredData.length === 0) return [];
        return yearFilteredData.filter(row =>
            (selectedCourse === 'All' || row[OFFICE_COLS.COURSE] === selectedCourse) &&
            (selectedMonth === 'All' || `${row.parsedDate.getFullYear()}-${String(row.parsedDate.getMonth() + 1).padStart(2, '0')}` === selectedMonth)
        );
    }, [yearFilteredData, selectedCourse, selectedMonth]);

    useEffect(() => {
        if (loading || (officeData.length === 0 && schoolData.length === 0 && !loading)) {
            setViewData({ revenue: 0, registrations: 0, chartData: [], subjects: [] });
            setCourseList([]);
            setMonthList([]);
            return;
        };

        // Generate Dropdown Lists based on YEAR-filtered data
        const courses = [...new Set(yearFilteredData.map(item => item[OFFICE_COLS.COURSE]))].sort();
        const months = [...new Set(yearFilteredData.map(item => `${item.parsedDate.getFullYear()}-${String(item.parsedDate.getMonth() + 1).padStart(2, '0')}`))]
            .map(monthKey => { const [year, month] = monthKey.split('-'); const date = new Date(year, month - 1, 1); return { value: monthKey, label: `${Object.keys(thaiMonths).find(k => thaiMonths[k] === date.getMonth())} ${date.getFullYear() % 100}` } }).sort((a,b) => b.value.localeCompare(a.value));
        setCourseList(courses);
        setMonthList(months);

        const totalRevenue = filteredData.reduce((sum, row) => sum + row.parsedIncome, 0);
        const totalRegistrations = filteredData.filter(row => row.type === 'office').length;
        const subjects = [...new Set(filteredData.map(item => item[OFFICE_COLS.COURSE]))].sort();
        const monthlyData = new Map();
        
        filteredData.forEach(row => {
            const monthKey = `${row.parsedDate.getFullYear()}-${String(row.parsedDate.getMonth() + 1).padStart(2, '0')}`;
            if (!monthlyData.has(monthKey)) {
                const date = row.parsedDate;
                const monthLabel = `${Object.keys(thaiMonths).find(k => thaiMonths[k] === date.getMonth())} ${date.getFullYear() % 100}`;
                const monthObject = { name: monthLabel, officeRevenue: 0, schoolRevenue: 0 };
                subjects.forEach(s => { monthObject[s] = 0; });
                monthlyData.set(monthKey, monthObject);
            }
            const currentMonth = monthlyData.get(monthKey);
            currentMonth[row[OFFICE_COLS.COURSE]] += row.parsedIncome;
            if (row.type === 'office') currentMonth.officeRevenue += row.parsedIncome;
            else if (row.type === 'school') currentMonth.schoolRevenue += row.parsedIncome;
        });

        const chartData = Array.from(monthlyData.values()).sort((a,b) => {
            const keyA = Array.from(monthlyData.keys()).find(key => monthlyData.get(key) === a);
            const keyB = Array.from(monthlyData.keys()).find(key => monthlyData.get(key) === b);
            return keyA.localeCompare(keyB);
        });
        
        setViewData({ revenue: Math.round(totalRevenue), registrations: totalRegistrations, chartData: chartData, subjects: subjects });
    }, [filteredData, yearFilteredData, combinedData, loading, officeData, schoolData]);

    const handleDataSourceChange = (event) => { setDataSources({ ...dataSources, [event.target.name]: event.target.checked }); };
    const handleGoBack = () => navigate('/main');

    if (loading) { 
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      );
    }
    
    const isAllSelected = selectedCourse === 'All' && selectedMonth === 'All';

    return (
      <Box className="dashboard-container" sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Box className="dashboard-top-header">
            <img 
              src="/Wisdom-Logo.png" 
              alt="Wisdom Logo" 
              className="header-logo" 
              onClick={handleGoBack}
              title="กลับหน้าหลัก"
            />
            
            <Box className="dashboard-main-title-container">
                <GradientHeader variant="h4" sx={{ margin: 0, padding: 0, whiteSpace: 'nowrap' }}>
                    รายรับปี
                </GradientHeader>
                <FormControl variant="standard">
                    <Select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(e.target.value)}
                        className="year-select"
                        disableUnderline
                    >
                        {availableYears.map(year => (
                            <MenuItem key={year} value={year}>{year}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
        </Box>
        
        <Box className="filters-container" sx={{ p: { xs: 2, md: 3 }, mb: 4 }}>
          <FormGroup sx={{ flexDirection: 'row', justifyContent: 'center', mb: 3 }}>
            <FormControlLabel control={<Checkbox checked={dataSources.office} onChange={handleDataSourceChange} name="office" />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><BusinessCenterIcon sx={{ mr: 1 }} />รายรับ Office</Box>} />
            <FormControlLabel control={<Checkbox checked={dataSources.school} onChange={handleDataSourceChange} name="school" />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><SchoolIcon sx={{ mr: 1 }} />รายรับ ร.ร.</Box>} />
          </FormGroup>
          <Grid container spacing={2} justifyContent="center">
            <Grid xs={12} sm="auto"><FormControl size="small" fullWidth><StyledSelect value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} displayEmpty><MenuItem value="All"><em>ทุกเดือน</em></MenuItem>{monthList.map(month => <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>)}</StyledSelect></FormControl></Grid>
            <Grid xs={12} sm="auto"><FormControl size="small" fullWidth><StyledSelect value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)} displayEmpty><MenuItem value="All"><em>ทุกวิชา</em></MenuItem>{courseList.map(course => <MenuItem key={course} value={course}>{course}</MenuItem>)}</StyledSelect></FormControl></Grid>
          </Grid>
        </Box>
        
        <Grid container spacing={3} sx={{ mb: 4 }} justifyContent="center">
            <Grid xs={12} md={6}><StatCard title="จำนวนการลงทะเบียน" value={viewData.registrations.toLocaleString()} icon={<PeopleAltIcon />} bgColor="linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)" /></Grid>
            <Grid xs={12} md={6}><StatCard title="รายได้รวม (บาท)" value={viewData.revenue.toLocaleString()} icon={<AttachMoneyIcon />} bgColor="linear-gradient(135deg, #00c853 0%, #64dd17 100%)" /></Grid>
        </Grid>
        
        <Card className="chart-card">
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#3f51b5', mb: 3 }}>
              {isAllSelected ? "ภาพรวมรายรับแต่ละเดือน" : "รายรับจำแนกตามวิชา"}
            </Typography>
            {isAllSelected ? (<TrendLineChart data={viewData.chartData} />) : (<GroupedBarChart data={viewData.chartData} subjects={viewData.subjects} />)}
          </CardContent>
        </Card>
        
        <Typography variant="body2" align="center" sx={{ mt: 3, color: '#78909c' }}>
          อัปเดตล่าสุด: {new Date().toLocaleDateString('th-TH')}
        </Typography>
      </Box>
    );
};

export default Dashboard;
